import { ActionGroup, Modal } from '@porsche-code/kado-ui';
import type { RadioGroupChangeEventDetail } from '@porsche-design-system/components-react';
import {
  PButton,
  PRadioGroup,
  PRadioGroupOption,
} from '@porsche-design-system/components-react';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import XLSX from 'xlsx';

import { NAMESPACES } from '@/config/i18n';

export const ExportModal = ({
  open,
  onClose,
  data,
  filename,
}: {
  open: boolean;
  onClose: () => void;
  data: Record<string, string>[];
  filename: string;
}) => {
  const { t } = useTranslation(NAMESPACES);
  const [format, setFormat] = useState<'csv' | 'xlsx'>('csv');

  const handleSubmit = () => {
    const completeFilename = `${filename} (${dayjs().format('YYYY-MM-DD')})`;
    const sanitizedData = data.map((item) =>
      Object.fromEntries(
        Object.entries(item).map(([key, value]) => [
          key,
          sanitizeCSVField(value),
        ]),
      ),
    );

    const ws = XLSX.utils.json_to_sheet(sanitizedData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws);

    switch (format) {
      case 'csv': {
        XLSX.writeFile(wb, `${completeFilename}.csv`, { FS: ';' });
        break;
      }
      case 'xlsx': {
        XLSX.writeFile(wb, `${completeFilename}.xlsx`);
        break;
      }
      default:
        throw new Error('Unknown export format.');
    }

    onClose();
  };

  return (
    <Modal open={open} onDismiss={onClose} size="small">
      <Modal.Header>{t('common:action.export')}</Modal.Header>
      <PRadioGroup
        direction="row"
        name="type"
        hideLabel
        value={format}
        onChange={(e: CustomEvent<RadioGroupChangeEventDetail>) => {
          const value = (e.target as HTMLInputElement).value as 'csv' | 'xlsx';
          setFormat(value);
        }}
      >
        <PRadioGroupOption
          value="xlsx"
          label={t('xlsx')}
          aria-label={t('xlsx')}
        />
        <PRadioGroupOption value="csv" label={t('csv')} aria-label={t('csv')} />
      </PRadioGroup>
      <ActionGroup>
        <PButton icon="close" onClick={() => onClose()} variant="secondary">
          {t('common:action.cancel')}
        </PButton>

        <PButton onClick={() => handleSubmit()}>
          {t('common:action.export')}
        </PButton>
      </ActionGroup>
    </Modal>
  );
};

const sanitizeCSVField = (value: string): string => {
  const cleaned = value.trim();
  return /^[=+\-@\t\r]/.test(cleaned) ? `'${cleaned}` : cleaned;
};
